import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Box, CircularProgress, CssBaseline } from '@mui/material';
import { AuthProvider, EMPLOYEE_GROUP_ID, useAuth } from './lib/auth';
import { UpdateController } from './components/UpdateController';
import { AppThemeProvider } from './lib/theme-context';
import { AppLayout } from './components/AppLayout';
import { Dashboard } from './pages/Dashboard';
import { MyDay } from './pages/employee/MyDay';
import { MyCalendar } from './pages/employee/MyCalendar';
import { MySwms } from './pages/employee/MySwms';
import { Login } from './pages/Login';
import { SetPassword } from './pages/SetPassword';
import { MODULES } from './config/nav';
import { Placeholder } from './pages/Placeholder';
import { Calendar } from './pages/Calendar';
import { Clients } from './pages/Clients';
import { Jobs } from './pages/Jobs';
import { Timesheet } from './pages/Timesheet';
import { Staff } from './pages/Staff';
import { OutstandingInvoicing } from './pages/OutstandingInvoicing';
import { OutstandingInvoiceCreate } from './pages/OutstandingInvoiceCreate';
import { Skillsets } from './pages/Skillsets';
import { ProgramSettings } from './pages/ProgramSettings';
import { CompanyDetails } from './pages/CompanyDetails';
import { StaffGroups } from './pages/StaffGroups';
import { Sms } from './pages/Sms';
import { Notes } from './pages/Notes';
import { Swms } from './pages/Swms';
import { DashboardPreview } from './pages/DashboardPreview';
import { CalendarPreview } from './pages/CalendarPreview';
import { ClientsPreview } from './pages/ClientsPreview';
import { JobsPreview } from './pages/JobsPreview';
import { TimesheetPreview } from './pages/TimesheetPreview';
import { StaffPreview } from './pages/StaffPreview';
import { SkillsetsPreview } from './pages/SkillsetsPreview';
import { ProgramSettingsPreview } from './pages/ProgramSettingsPreview';
import { CompanyDetailsPreview } from './pages/CompanyDetailsPreview';
import { StaffGroupsPreview } from './pages/StaffGroupsPreview';
import { OutstandingInvoicingPreview } from './pages/OutstandingInvoicingPreview';
import { SmsPreview } from './pages/SmsPreview';
import { NotesPreview } from './pages/NotesPreview';
import { SwmsPreview } from './pages/SwmsPreview';

function Centred(): React.JSX.Element {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <CircularProgress size={28} />
        </Box>
    );
}

/**
 * Route gate. Two checks, in order: is there a session, and does this user hold
 * the permission the route requires.
 *
 * Hiding a tile is presentation; this is what stops someone typing the URL. The
 * API enforces the same slug per endpoint, so neither layer is load-bearing
 * alone.
 */
function Protected({ slug, children }: { slug?: string; children: React.JSX.Element }): React.JSX.Element {
    const { session, me, loading, can } = useAuth();

    if (loading) {
        return <Centred />;
    }
    if (session === null) {
        return <Navigate to="/login" replace />;
    }
    if (me === null) {
        return <Centred />;
    }
    if (slug !== undefined && !can(slug)) {
        return <Navigate to="/" replace />;
    }
    return <AppLayout>{children}</AppLayout>;
}

/**
 * What `/` shows.
 *
 * Field staff — the Employee role, 56 of 59 people — go to the employee app,
 * which is where the legacy app sent them: a `staff_level` 2 login landed on
 * Backend_employeedashboard, not the dashboard. Everyone else gets the tile
 * grid, with a My Day tile on it.
 *
 * Keyed on the ROLE, not on "holds no modules". The Employee role is seeded
 * with calendar, jobs, timesheet and notes, so a module count is never zero for
 * the very people this is meant to catch.
 *
 * There is deliberately NO link from the employee app to the office dashboard.
 * An earlier version had one, and an employee following it saw company-wide job
 * and staff counts — not what the phone app is for. Both `/` and `/dashboard`
 * now run through this gate.
 *
 * This is presentation. The API gates every endpoint on its own permission, so
 * an employee typing /jobs still reaches whatever `role_permissions` grants the
 * Employee role — today that is calendar, jobs, timesheet and notes. If field
 * staff should not see those at all, the fix is on the Staff Groups screen, not
 * here.
 */
function Landing(): React.JSX.Element {
    const { me } = useAuth();
    return me?.staffGroupId === EMPLOYEE_GROUP_ID ? <Navigate to="/my" replace /> : <Dashboard />;
}

function AppRoutes(): React.JSX.Element {
    const { session, loading } = useAuth();

    return (
        <Routes>
            <Route
                path="/login"
                element={loading ? <Centred /> : session !== null ? <Navigate to="/" replace /> : <Login />}
            />
            {/* Where invite and password-reset emails land. Deliberately not
                Protected: an invite link arrives with a session but no password
                yet, and an expired one arrives with no session at all -- the
                page handles both, so a redirect to /login would break it. */}
            <Route path="/set-password" element={<SetPassword />} />
            <Route
                path="/"
                element={
                    <Protected>
                        <Landing />
                    </Protected>
                }
            />
            {/* /dashboard shares the same gate as `/`, so it cannot become a
                second, unguarded way in. Field staff are sent back to /my: an employee reached the office
                dashboard through a "Office view" link on My Day and saw
                company-wide job and staff counts, which is not what that screen
                is for. The link is gone and this is the second lock on the door.

                It is presentation, not authorisation. The API gates every
                endpoint on its own permission — that is why Uninvoiced showed
                dashes rather than a figure — and the counts that DID appear are
                ones the Employee role genuinely holds `jobs` and `timesheet`
                for. Narrowing that is a change to role_permissions, not code. */}
            <Route
                path="/dashboard"
                element={
                    <Protected>
                        <Landing />
                    </Protected>
                }
            />
            {/* ─── The employee app ─────────────────────────────────────────
                Deliberately NOT gated on a permission slug. Clocking on, seeing
                your own day and signing a SWMS are things every staff member
                does for themselves, whatever admin modules they hold — the API
                scopes each call to the signed-in staff id rather than checking a
                slug. See apps/api/src/routes/employee.ts. */}
            <Route
                path="/my"
                element={
                    <Protected>
                        <MyDay />
                    </Protected>
                }
            />
            <Route
                path="/my/calendar"
                element={
                    <Protected>
                        <MyCalendar />
                    </Protected>
                }
            />
            <Route
                path="/my/swms/:jobId"
                element={
                    <Protected>
                        <MySwms />
                    </Protected>
                }
            />
            {/* One route per declared module. Each currently renders a placeholder;
                they are replaced module by module, in the order set out in the
                port plan (Calendar first, then Timesheet, Jobs, and so on). */}
            {/* Create Invoice, opened from Outstanding Invoicing with the ticked entries. */}
            <Route
                path="/outstanding-invoicing/invoice"
                element={
                    <Protected slug="outstanding-invoicing">
                        <OutstandingInvoiceCreate />
                    </Protected>
                }
            />
            {MODULES.map((m) => (
                <Route
                    key={m.slug}
                    path={m.path}
                    element={
                        <Protected slug={m.slug}>
                            {m.slug === 'calendar' ? (
                                <Calendar />
                            ) : m.slug === 'clients' ? (
                                <Clients />
                            ) : m.slug === 'outstanding-invoicing' ? (
                                <OutstandingInvoicing />
                            ) : m.slug === 'jobs' ? (
                                <Jobs />
                            ) : m.slug === 'timesheet' ? (
                                <Timesheet />
                            ) : m.slug === 'staff' ? (
                                <Staff />
                            ) : m.slug === 'skillsets' ? (
                                <Skillsets />
                            ) : m.slug === 'program-settings' ? (
                                <ProgramSettings />
                            ) : m.slug === 'company-details' ? (
                                <CompanyDetails />
                            ) : m.slug === 'staff-groups' ? (
                                <StaffGroups />
                            ) : m.slug === 'sms' ? (
                                <Sms />
                            ) : m.slug === 'notes' ? (
                                <Notes />
                            ) : m.slug === 'swms' ? (
                                <Swms />
                            ) : (
                                <Placeholder title={m.label} description={m.description} />
                            )}
                        </Protected>
                    }
                />
            ))}
            {/* Dev-only: review the design without a database. Stripped from
                production builds by the import.meta.env.DEV guard. */}
            {import.meta.env.DEV ? <Route path="/preview" element={<DashboardPreview />} /> : null}
            {import.meta.env.DEV ? <Route path="/preview/calendar" element={<CalendarPreview />} /> : null}
            {import.meta.env.DEV ? <Route path="/preview/clients" element={<ClientsPreview />} /> : null}
            {import.meta.env.DEV ? <Route path="/preview/jobs" element={<JobsPreview />} /> : null}
            {import.meta.env.DEV ? <Route path="/preview/timesheet" element={<TimesheetPreview />} /> : null}
            {import.meta.env.DEV ? <Route path="/preview/staff" element={<StaffPreview />} /> : null}
            {import.meta.env.DEV ? <Route path="/preview/skillsets" element={<SkillsetsPreview />} /> : null}
            {import.meta.env.DEV ? (
                <Route path="/preview/program-settings" element={<ProgramSettingsPreview />} />
            ) : null}
            {import.meta.env.DEV ? <Route path="/preview/company" element={<CompanyDetailsPreview />} /> : null}
            {import.meta.env.DEV ? <Route path="/preview/staff-groups" element={<StaffGroupsPreview />} /> : null}
            {import.meta.env.DEV ? (
                <Route path="/preview/outstanding-invoicing/*" element={<OutstandingInvoicingPreview />} />
            ) : null}
            {import.meta.env.DEV ? <Route path="/preview/sms" element={<SmsPreview />} /> : null}
            {import.meta.env.DEV ? <Route path="/preview/notes" element={<NotesPreview />} /> : null}
            {import.meta.env.DEV ? <Route path="/preview/swms" element={<SwmsPreview />} /> : null}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export function App(): React.JSX.Element {
    return (
        <AppThemeProvider>
            <CssBaseline />
            <BrowserRouter>
                <AuthProvider>
                    <AppRoutes />
                    <UpdateController />
                </AuthProvider>
            </BrowserRouter>
        </AppThemeProvider>
    );
}
